import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { BaseTimePeriod } from '@services/FiltersTypes';
import {
  getDateFromPeriod,
  getPreviousMonth,
  getPreviousWeek,
  getWednesdayOfWeek,
  isCustomWeeks,
} from '@utils/PeriodUtils';
import { PeriodSetter } from '@components/range-picker/RangePicker';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';

export interface UseDateRangePickerCallbackParam {
  startPeriod: Date;
  endPeriod: Date;
  setStartPeriod: Dispatch<SetStateAction<Date>>;
  setEndPeriod: Dispatch<SetStateAction<Date>>;
}

export interface UseDateRangePickerHandlerCallbackParam {
  periodFrom: Date;
  periodTo: Date;
  comparisonPeriodFrom: Date;
  comparisonPeriodTo: Date;
  setPeriodFrom: Dispatch<SetStateAction<Date>>;
  setPeriodTo: Dispatch<SetStateAction<Date>>;
  setComparisonPeriodFrom: Dispatch<SetStateAction<Date>>;
  setComparisonPeriodTo: Dispatch<SetStateAction<Date>>;
  periodicity: Periodicity;
}

export interface UseDateRangePickerHandlerCallbackSetter {
  setPeriodFromHandler: PeriodSetter;
  setPeriodToHandler: PeriodSetter;
  setComparisonPeriodFromHandler: PeriodSetter;
  setComparisonPeriodToHandler: PeriodSetter;
}

/**
 * Returns a memoized setter function for a date state, adjusting the date if the periodicity is custom weeks.
 *
 * This hook ensures that when the periodicity is set to custom weeks, the date is normalized to the Wednesday of that week,
 * which is important for consistent week-based period selection in the UI. For other periodicities, the date is set as-is.
 *
 * @param {Periodicity} periodicity - The current periodicity (e.g., monthly, weekly, custom weeks).
 * @param {Dispatch<SetStateAction<Date>>} setPeriod - The state setter function for the date.
 * @returns {Dispatch<SetStateAction<Date>>} - A memoized setter that applies the correct date transformation.
 *
 * @example
 * const setStartPeriod = usePeriodSetter(periodicity, setStartPeriodState);
 * setStartPeriod(new Date()); // If periodicity is custom weeks, sets to the Wednesday of the week.
 *
 * // Usage in a component:
 * const setPeriod = usePeriodSetter(Periodicity.CustomWeeks, setDate);
 * setPeriod(new Date('2024-06-01')); // Will set the date to the Wednesday of that week.
 *
 * @important
 * Use this hook whenever you need to update a date state that depends on the periodicity, especially when supporting custom week logic.
 * It prevents inconsistent date handling and ensures UI and business logic remain in sync with the selected time frame.
 */
const usePeriodSetter = (
  periodicity: Periodicity,
  setPeriod: Dispatch<SetStateAction<Date>>,
): Dispatch<SetStateAction<Date>> =>
  useCallback<Dispatch<SetStateAction<Date>>>(
    (dateOrUpdater) => {
      const date = typeof dateOrUpdater === 'function' ? (dateOrUpdater as () => Date)() : dateOrUpdater;
      if (isCustomWeeks(periodicity)) {
        setPeriod(getWednesdayOfWeek(date));
      } else {
        setPeriod(date);
      }
    },
    [periodicity, setPeriod],
  );

export const useDateRangePickerCallBack = (
  currentPeriod: BaseTimePeriod,
  periodicity: Periodicity,
): UseDateRangePickerCallbackParam => {
  const [parsedStart, parsedEnd] = useMemo(() => {
    const [startStr, endStr] = currentPeriod.label.split(' - ');
    return [getDateFromPeriod(startStr), getDateFromPeriod(endStr)];
  }, [currentPeriod.label]);

  const [startPeriod, setStartPeriod] = useState<Date>(() => parsedStart);
  const [endPeriod, setEndPeriod] = useState<Date>(() => parsedEnd);

  /**
   * Returns a memoized setter for the start period, ensuring the date is adjusted to Wednesday if periodicity is custom weeks.
   */
  const setStartPeriodState = usePeriodSetter(periodicity, setStartPeriod);
  /**
   * Returns a memoized setter for the end period, ensuring the date is adjusted to Wednesday if periodicity is custom weeks.
   */
  const setEndPeriodState = usePeriodSetter(periodicity, setEndPeriod);

  useEffect(() => {
    setStartPeriodState(parsedStart);
    setEndPeriodState(parsedEnd);
  }, [parsedStart, parsedEnd, setStartPeriodState, setEndPeriodState]);

  return useMemo<UseDateRangePickerCallbackParam>(() => {
    return {
      startPeriod,
      endPeriod,
      setStartPeriod: setStartPeriodState,
      setEndPeriod: setEndPeriodState,
    };
  }, [startPeriod, endPeriod, setStartPeriodState, setEndPeriodState]);
};

export const useDateRangePickerHandlerCallback: (
  useDateRangePickerHandlerCallbackParam: UseDateRangePickerHandlerCallbackParam,
) => UseDateRangePickerHandlerCallbackSetter = (
  useDateRangePickerHandlerCallbackParam: UseDateRangePickerHandlerCallbackParam,
) => {
  const {
    periodTo,
    comparisonPeriodFrom,
    comparisonPeriodTo,
    setPeriodFrom,
    setPeriodTo,
    setComparisonPeriodFrom,
    setComparisonPeriodTo,
    periodicity,
  } = useDateRangePickerHandlerCallbackParam;

  const getPreviousPeriod = useCallback(
    (date: Date) => (isCustomWeeks(periodicity) ? getPreviousWeek(date, 1) : getPreviousMonth(date, 1)),
    [periodicity],
  );

  const setPeriodFromHandler = useCallback<PeriodSetter>(
    (selectedPeriod) => {
      setPeriodFrom(selectedPeriod);
    },
    [setPeriodFrom],
  );

  const setPeriodToHandler = useCallback<PeriodSetter>(
    (selectedPeriod: Date) => {
      if (isNaN(selectedPeriod?.getTime())) {
        return;
      }

      if (validateDateLimit(comparisonPeriodTo, selectedPeriod)) {
        setComparisonPeriodTo(getPreviousPeriod(selectedPeriod));
      }

      if (validateDateLimit(comparisonPeriodFrom, selectedPeriod)) {
        setComparisonPeriodFrom(getPreviousPeriod(selectedPeriod));
      }

      setPeriodTo(selectedPeriod);
    },
    [
      setPeriodTo,
      comparisonPeriodTo,
      comparisonPeriodFrom,
      periodTo,
      setComparisonPeriodTo,
      setComparisonPeriodFrom,
      getPreviousPeriod,
    ],
  );

  const setComparisonPeriodFromHandler = useCallback<PeriodSetter>(
    (selectedPeriod) => {
      setComparisonPeriodFrom(selectedPeriod);
    },
    [setComparisonPeriodFrom],
  );

  const setComparisonPeriodToHandler = useCallback<PeriodSetter>(
    (selectedPeriod) => {
      if (isNaN(selectedPeriod?.getTime())) {
        return;
      }

      setComparisonPeriodTo(selectedPeriod);
    },
    [setComparisonPeriodTo],
  );

  return {
    setPeriodFromHandler,
    setPeriodToHandler,
    setComparisonPeriodFromHandler,
    setComparisonPeriodToHandler,
  };
};

/**
 *
 * @param comparisonPeriodDate is the date to be compared
 * @param selectedDate is the selected date in the Focus Period End Date
 * return true if comparison period start date or end date is more than focus period start date
 * else return false
 */
const validateDateLimit = (comparisonPeriodDate: Date, selectedDate: Date) => {
  return (
    (comparisonPeriodDate.getFullYear() === selectedDate.getFullYear() &&
      comparisonPeriodDate.getMonth() >= selectedDate.getMonth()) ||
    comparisonPeriodDate.getFullYear() > selectedDate.getFullYear()
  );
};
